<template>
  <div class="overflow-hidden relative min-h-[60vh]">
    <!-- Dragged Items Preview -->
    <Transition name="fade">
      <div v-if="isDragging && draggedItems.length > 1" class="fixed inset-0 pointer-events-none z-[1000]">
        <TransitionGroup name="fade" tag="div" @after-leave="cleanupDragState">
          <div 
            v-for="(item, index) in draggedItems" 
            :key="item.id"
            class="multi-drag-item"
            :style="{
              '--drag-x': `${dragStartPosition.x + index * 4}px`,
              '--drag-y': `${dragStartPosition.y + index * 4}px`
            }"
          >
            <div class="max-w-[200px] truncate">
              {{ item.task.name }}
            </div>
          </div>
        </TransitionGroup>
      </div>
    </Transition>

    <!-- Skeleton loading state until content is ready -->
    <CurriculumSkeleton 
      v-if="!isContentFullyReady" 
      :week-count="3" 
    />

    <!-- Main content -->
    <div 
      v-show="isContentFullyReady"
      ref="contentRoot"
    >
      <!-- Loading message -->
      <div 
        v-if="(!isInitialLoad && props.isLoading && !isReordering) || isReordering" 
        class="fixed bottom-4 left-4 z-40 bg-white rounded-lg px-4 py-2 flex items-center gap-2 border border-gray-200"
      >
        <Loader2 class="w-4 h-4 animate-spin text-gray-600" />
        <span class="text-sm text-gray-600 font-medium">
          {{ isReordering ? 'Saving new order...' : 'Loading...' }}
        </span>
      </div>

      <!-- Success message -->
      <div 
        v-if="showSuccess" 
        class="fixed bottom-4 left-4 z-50 bg-green-50 rounded-lg px-4 py-2 flex items-center gap-2 border border-green-200"
      >
        <CheckCircle2 class="w-4 h-4 text-green-600" />
        <span class="text-sm text-green-600 font-medium">Saved successfully</span>
      </div>

      <!-- Error message -->
      <div 
        v-if="showError" 
        class="fixed bottom-4 left-4 z-50 bg-red-50 rounded-lg px-4 py-2 flex items-center gap-2 border border-red-200"
      >
        <XCircle class="w-4 h-4 text-red-600" />
        <span class="text-sm text-red-600 font-medium">Failed to save changes</span>
      </div>

      <!-- Loop through weeks -->
      <div class="space-y-6">
        <!-- Weeks -->
        <div v-for="(items, week) in weekItems" :key="week" :id="`week-${week}`" ref="weekElements">
          <div class="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <!-- Week header with points -->
            <div class="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div class="flex justify-between items-center">
                <div class="flex items-center gap-3">
                  <h3 class="text-sm font-medium text-gray-700">Week {{ week }}</h3>
                  <span class="text-sm text-gray-500">({{ items.length }} tasks)</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-xs text-gray-500">Points:</span>
                  <span class="text-sm font-medium text-cosmicPurple">{{ calculateWeekPoints(items) }}</span>
                </div>
              </div>
            </div>

            <!-- Add skeleton loader for initial load -->
            <table v-if="isInitialLoad && props.isLoading" class="min-w-full divide-y divide-gray-200">
              <thead class="bg-white">
                <tr>
                  <th v-for="i in 9" :key="i" scope="col" class="py-3">
                    <div class="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="i in 3" :key="i" class="divide-x divide-gray-200">
                  <td v-for="j in 9" :key="j" class="py-4">
                    <div class="h-4 bg-gray-100 rounded animate-pulse"></div>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Actual table content -->
            <table v-else class="min-w-full divide-y divide-gray-200">
              <thead class="bg-white">
                <tr>
                  <th
                    v-if="canEdit"
                    scope="col"
                    class="w-10 py-3 text-xs font-normal text-gray-500 text-center"
                    @click.stop
                  >
                    <Checkbox
                      :model-value="weekItems[week]?.every(item => selectedItems.has(item.id))"
                      @update:model-value="(checked) => toggleAllInWeek(week, checked)"
                    />
                  </th>
                  <th
                    scope="col"
                    class="w-8 py-3 text-xs font-normal text-gray-500 text-center"
                  >
                    #
                  </th>
                  <th
                    scope="col"
                    class="w-14 py-3 text-xs font-normal text-gray-500 text-center"
                  >
                    Section
                  </th>
                  <th
                    scope="col"
                    class="py-3 text-left text-xs font-normal text-gray-500 pl-2"
                  >
                    Title
                  </th>
                  <th
                    scope="col"
                    class="w-16 py-3 text-center text-xs font-normal text-gray-500"
                  >
                    Reviews
                  </th>
                  <th
                    scope="col"
                    class="w-16 py-3 text-xs font-normal text-gray-500 text-center"
                  >
                    Points
                  </th>
                  <th
                    scope="col"
                    class="w-16 py-3 text-xs font-normal text-gray-500 text-center"
                  >
                    Required
                  </th>
                  <th
                    scope="col"
                    class="w-16 py-3 text-xs font-normal text-gray-500 text-center"
                  >
                    Schedule
                  </th>
                  <th
                    v-if="canEdit"
                    scope="col"
                    class="w-16 py-3 text-center text-xs font-normal text-gray-500"
                  >
                    Edit
                  </th>
                  <th
                    v-if="canEdit"
                    scope="col"
                    class="w-16 py-3 text-center text-xs font-normal text-gray-500"
                  >
                    Delete
                  </th>
                </tr>
              </thead>
              <Draggable
                v-model="weekItems[week]"
                :component-data="draggableComponentData"
                item-key="id"
                handle=".drag-handle"
                :animation="300"
                @change="(e: DraggableChangeEvent) => handleReorder(e, week)"
                :disabled="!canEdit || props.isLoading || isReordering"
                :delay="50"
                :delay-on-touch-only="true"
                :group="'path-steps'"
                :drag-class="'dragging-item'"
                :chosen-class="'chosen-item'"
                :ghost-class="'ghost-item'"
                tag="tbody"
                class="bg-white divide-y divide-gray-200"
              >
                <template #item="{ element: item, index }">
                  <tr 
                    class="transition-all duration-200 relative group cursor-pointer"
                    @click="toggleExpand(item.id)"
                    :class="{ 
                      'z-10': expandedItemId === item.id,
                      'bg-cosmicPurple/20 hover:bg-cosmicPurple/25': changedItemIds.has(item.id) && !selectedItems.has(item.id),
                      'bg-cosmicPurple/30 hover:bg-cosmicPurple/35': selectedItems.has(item.id),
                      'hover:bg-cosmicPurple/10': !changedItemIds.has(item.id) && !selectedItems.has(item.id)
                    }"
                  >
                    <td 
                      v-if="canEdit"
                      class="py-4 w-10 text-sm text-center"
                      @click.stop
                    >
                      <Checkbox
                        :model-value="selectedItems.has(item.id)"
                        @update:model-value="(checked) => toggleItemSelection(item.id, checked)"
                      />
                    </td>
                    <td
                      class="py-4 w-10 text-sm text-gray-400 text-center"
                      :class="canEdit ? 'drag-handle cursor-move' : ''"
                    >
                      <div class="flex items-center justify-center">
                        <GripVertical v-if="canEdit" class="w-4 h-4 ml-1 text-gray-400" />
                        <span>{{ index + 1 }}</span>
                      </div>
                    </td>
                    <td class="py-4 w-14 whitespace-normal text-sm text-center">
                      <div class="flex justify-center items-center" v-if="props.getSectionIcon && props.hoverCardComponents">
                        <component :is="props.hoverCardComponents.HoverCard" v-if="item?.task?.section?.name && props.getSectionIcon(item?.task?.section?.name)">
                          <component :is="props.hoverCardComponents.HoverCardTrigger">
                            <component
                              :is="props.getSectionIcon(item?.task?.section?.name)"
                              class="w-5 h-5 text-gray-500 cursor-pointer"
                            />
                          </component>
                          <component :is="props.hoverCardComponents.HoverCardContent" class="w-auto p-2 text-sm">
                            {{ item?.task?.section?.name }}
                          </component>
                        </component>
                      </div>
                    </td>
                    <td class="py-4 whitespace-normal text-sm text-gray-900 pl-2">
                      <a
                        v-if="item?.task?.url && isUrl(item?.task?.url)"
                        :href="item?.task?.url"
                        target="_blank"
                        class="text-gray-900 hover:text-cosmicPurple inline-flex items-center transition-all duration-200"
                        @click.stop
                      >
                        <span>{{ item?.task?.name || '' }}</span>
                        <ExternalLink class="w-3 h-3 ml-1 shrink-0 text-cosmicPurple" />
                      </a>
                      <template v-else>
                        <span>{{ item?.task?.name || '' }}</span>
                      </template>
                    </td>
                    <td class="py-4 whitespace-normal text-sm text-gray-900 text-center">
                      <div v-if="item?.task?.review_required === false" class="flex items-center justify-center">
                        <OctagonMinus class="w-5 h-5 text-gray-700" />
                      </div>
                      <div v-else-if="item?.review_stats" class="flex items-center justify-center gap-1">
                        <span class="text-yellow-500">★</span>
                        <span>{{ item.review_stats.average_rating.toFixed(1) }}</span>
                        <span class="text-gray-400 text-xs">({{ item.review_stats.total_reviews }})</span>
                      </div>
                      <span v-else class="text-gray-400">-</span>
                    </td>
                    <td class="py-4 whitespace-normal text-sm text-gray-900 text-center">
                      {{ item?.points || 0 }}
                    </td>
                    <td class="py-4 whitespace-normal text-sm text-gray-900 text-center">
                      <ShieldCheck v-if="item?.is_required" class="w-4 h-4 mx-auto" />
                    </td>
                    <td class="py-4 whitespace-normal text-sm text-gray-900 text-center">
                      <RefreshCcw v-if="item?.has_external_dependency" class="w-4 h-4 mx-auto text-gray-500" />
                    </td>
                    <td v-if="canEdit" class="py-4 text-center">
                      <button 
                        class="p-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
                        @click.stop="toggleExpand(item.id)"
                      >
                        <Edit2 class="w-4 h-4" />
                      </button>
                    </td>
                    <td v-if="canEdit" class="py-4 text-center">
                      <button 
                        class="p-1.5 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors"
                        @click.stop="confirmDelete(item)"
                      >
                        <Trash2 class="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                </template>
              </Draggable>
            </table>
          </div>
        </div>

        <!-- Expanded Content Overlay -->
        <ExpeditionStepExpanded
          v-if="expandedItemId !== null"
          :expanded-item-id="expandedItemId"
          :item="Object.values(weekItems).flat().find(item => item.id === expandedItemId)"
          :can-edit="canEdit"
          @close="expandedItemId = null"
          @saved="handleSaved"
        />

        <!-- Delete Confirmation Modal -->
        <DeleteConfirmationModal
          v-if="canEdit"
          :is-open="showDeleteModal"
          :item-name="itemToDelete?.task?.name || ''"
          :is-deleting="isDeleting"
          @confirm="deleteItem"
          @cancel="showDeleteModal = false"
        />

        <!-- Batch Edit Button -->
        <div 
          v-if="showBatchEditButton"
          class="fixed bottom-4 right-44 z-[100]"
        >
          <button
            @click="openBatchEdit"
            class="bg-cosmicPurple hover:bg-cosmicPurple/90 text-white text-sm px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 transition-all duration-200"
          >
            <Users class="w-4 h-4" />
            <span>Batch Edit {{ selectedItems.size > 0 ? `(${selectedItems.size})` : '' }}</span>
          </button>
        </div>

        <!-- Floating Save Button -->
        <div 
          v-if="canEdit && hasUnsavedChanges"
          class="fixed bottom-4 right-4 z-[100]"
        >
          <button
            @click="saveChanges"
            :disabled="isReordering"
            class="bg-cosmicPurple hover:bg-cosmicPurple/90 text-white text-sm px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save class="w-4 h-4" />
            <span>Save Changes</span>
          </button>
        </div>

        <!-- Batch Edit Component -->
        <BatchStepEdit
          v-if="canEdit && showBatchEdit"
          :items="Object.values(weekItems).flat().filter(item => selectedItems.has(item.id))"
          @close="showBatchEdit = false"
          @saved="handleBatchSaved"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, defineProps, defineEmits, computed, onMounted, watch, onUnmounted, nextTick } from 'vue'
import {
  ExternalLink,
  Loader2,
  Edit2,
  Trash2,
  ShieldCheck,
  GripVertical,
  CheckCircle2,
  XCircle,
  OctagonMinus,
  Save,
  Users,
  RefreshCcw
} from 'lucide-vue-next'
import Draggable from 'vuedraggable'
import { isUrl } from '@/utils/isUrl'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal.vue'
import { usePathStepsStore } from '@/stores/pathSteps'
import { groupAndSortByWeek } from '@/utils/curriculumSorting'
import ExpeditionStepExpanded from './ExpeditionStepExpanded.vue'
import BatchStepEdit from './BatchStepEdit.vue'
import CurriculumSkeleton from './CurriculumSkeleton.vue'
import { Checkbox } from '@/components/ui/checkbox'
import type { WeeklyTaskItem, DraggableChangeEvent, HoverCardComponents, PathStepChange } from '@/types/curriculum'

// Define Props using the new structure
const props = defineProps<{
  pathStepsByWeek:
    | {
        path_steps?: Record<string, WeeklyTaskItem[]>
      }
    | Record<string, WeeklyTaskItem[]>
    | any // Accept different potential formats
  isLoading: boolean
  getSectionIcon?: (sectionName: string) => any
  hoverCardComponents?: HoverCardComponents
  canEdit?: boolean
}>()

const canEdit = computed(() => props.canEdit !== false)

// Computed property to structure the data correctly regardless of input format
const processedData = computed(() => {
  if (!props.pathStepsByWeek) return {}

  let items: WeeklyTaskItem[] = []

  // If data has the path_steps structure
  if (props.pathStepsByWeek.path_steps) {
    items = Object.values(props.pathStepsByWeek.path_steps).flat() as WeeklyTaskItem[]
  }
  // If data is already in week-structured format
  else if (typeof props.pathStepsByWeek === 'object' && !Array.isArray(props.pathStepsByWeek)) {
    items = Object.values(props.pathStepsByWeek).flat() as WeeklyTaskItem[]
  }
  // If it's an array, use it directly
  else if (Array.isArray(props.pathStepsByWeek)) {
    items = props.pathStepsByWeek
  }

  return groupAndSortByWeek(items)
})

// Create a reactive ref for each week's items
const weekItems = ref<Record<string, WeeklyTaskItem[]>>({})

// Add new state management
const originalState = ref<Record<string, WeeklyTaskItem[]>>({})
const hasUnsavedChanges = ref(false)
const changedItemIds = ref<Set<number>>(new Set())
const isReordering = ref(false)

// Add selection state
const selectedItems = ref<Set<number>>(new Set())

// Update the event handlers
const toggleItemSelection = (itemId: number, checked: boolean | 'indeterminate') => {
  if (!canEdit.value) return
  if (checked === 'indeterminate') return
  if (checked) {
    selectedItems.value.add(itemId)
  } else {
    selectedItems.value.delete(itemId)
  }
}

const toggleAllInWeek = (week: string, checked: boolean | 'indeterminate') => {
  if (!canEdit.value) return
  if (checked === 'indeterminate') return
  const weekItemIds = weekItems.value[week].map(item => item.id)
  
  if (checked) {
    weekItemIds.forEach(id => selectedItems.value.add(id))
  } else {
    weekItemIds.forEach(id => selectedItems.value.delete(id))
  }
}

// Watch for changes in selectedItems
watch(selectedItems, (newValue) => {
  console.log('Selected items changed:', Array.from(newValue))
}, { deep: true })

// Computed for batch edit button visibility
const showBatchEditButton = computed(() => canEdit.value && selectedItems.value.size > 0 && !showBatchEdit.value)

// Add batch edit state
const showBatchEdit = ref(false)

const openBatchEdit = () => {
  if (!canEdit.value) return
  console.log('Opening batch edit')
  showBatchEdit.value = true
}

const handleBatchSaved = () => {
  showBatchEdit.value = false
  selectedItems.value.clear()
  emit('refresh-data')
}

// Reset selection when data changes
watch(() => props.pathStepsByWeek, () => {
  selectedItems.value.clear()
}, { deep: true })



// Watch for changes in processedData and update weekItems and originalState
watch(() => props.pathStepsByWeek, () => {
  if (!isReordering.value) {
    setTimeout(() => {
      if (!isReordering.value) {
        weekItems.value = { ...processedData.value }
        // Store original state for comparison
        originalState.value = JSON.parse(JSON.stringify(processedData.value))
        // Reset change tracking
        hasUnsavedChanges.value = false
        changedItemIds.value = new Set()
      }
    }, 50)
  }
}, { deep: true, immediate: true })

// Add new ref for showing error message
const showError = ref(false)
const showSuccess = ref(false)

// Add helper to track modified weeks
const modifiedWeeks = ref(new Set<string>())

// Update handleReorder to track modified weeks
const handleReorder = async (event: DraggableChangeEvent, currentWeek: string) => {
  if (!canEdit.value) return
  const moveEvent = event.moved || event.added
  if (!moveEvent) return

  // If the moved item is selected and there are other selected items
  if (selectedItems.value.has(moveEvent.element.id) && selectedItems.value.size > 1) {
    const selectedItemsList = Array.from(selectedItems.value) as number[]
    const targetIndex = moveEvent.newIndex
    const targetWeek = currentWeek
    
    // Get all selected items from their current positions
    const itemsToMove = selectedItemsList.map(id => {
      let foundItem: WeeklyTaskItem | null = null
      let foundWeek = ''
      
      Object.entries(weekItems.value).forEach(([week, items]) => {
        const item = items.find(i => i.id === id)
        if (item) {
          foundItem = { ...item }
          foundWeek = week
        }
      })
      
      return { item: foundItem as WeeklyTaskItem | null, sourceWeek: foundWeek }
    }).filter((x): x is { item: WeeklyTaskItem; sourceWeek: string } => x.item !== null)

    // Track source weeks as modified
    itemsToMove.forEach(({ sourceWeek }) => {
      modifiedWeeks.value.add(sourceWeek)
    })
    
    // Remove all selected items from their current positions
    Object.keys(weekItems.value).forEach(week => {
      weekItems.value[week] = weekItems.value[week].filter(item => !selectedItems.value.has(item.id))
    })

    // Insert all selected items at the target position
    itemsToMove.forEach((moveItem, index) => {
      // Create the week array if it doesn't exist
      if (!weekItems.value[targetWeek]) {
        weekItems.value[targetWeek] = []
      }
      weekItems.value[targetWeek].splice(targetIndex + index, 0, moveItem.item)
      changedItemIds.value.add(moveItem.item.id)
    })

    // Mark target week as modified
    modifiedWeeks.value.add(targetWeek)
  } else {
    // Single item move - mark only the moved item as changed
    changedItemIds.value.add(moveEvent.element.id)
    modifiedWeeks.value.add(currentWeek)
  }

  hasUnsavedChanges.value = true
}

// Update the drag state management
const isDragging = ref(false)
const dragStartPosition = ref({ x: 0, y: 0 })
const draggedItems = ref<WeeklyTaskItem[]>([])

// Force cleanup of drag state
const cleanupDragState = () => {
  window.removeEventListener('mousemove', onMouseMove)
  window.removeEventListener('mouseup', onMouseUp)
  isDragging.value = false
  draggedItems.value = []
  dragStartPosition.value = { x: 0, y: 0 }
}

// Add mouse up handler
const onMouseUp = () => {
  cleanupDragState()
}

// Update mouse move handler
const onMouseMove = (e: MouseEvent) => {
  if (!isDragging.value) {
    cleanupDragState()
    return
  }
  dragStartPosition.value = {
    x: e.clientX,
    y: e.clientY
  }
}

// Update drag handlers
const onDragStart = (evt: DragEvent) => {
  if (!evt.target) return
  
  // Clean up any existing listeners first
  cleanupDragState()
  
  isDragging.value = true
  if (evt.clientX && evt.clientY) {
    dragStartPosition.value = {
      x: evt.clientX,
      y: evt.clientY
    }
  }
  
  // Set dragged items for visual feedback
  draggedItems.value = Object.values(weekItems.value)
    .flat()
    .filter(item => selectedItems.value.has(item.id))

  // Add event listeners
  window.addEventListener('mousemove', onMouseMove)
  window.addEventListener('mouseup', onMouseUp)
}

const onDragEnd = () => {
  cleanupDragState()
}

// Add cleanup on component unmount
onUnmounted(() => {
  cleanupDragState()
})

// Update the draggableComponentData computed
const draggableComponentData = computed(() => ({
  tag: 'tbody',
  type: 'transition-group',
  name: !isReordering.value ? 'flip-list' : null,
  'onDragstart': onDragStart,
  'onDragend': onDragEnd
}))

// Update save changes function to send all items from modified weeks
const saveChanges = async () => {
  if (!canEdit.value) return
  try {
    isReordering.value = true
    showSuccess.value = false
    
    // Reset drag state
    cleanupDragState()
    
    // Collect changes for all items in modified weeks
    const changes: PathStepChange[] = []
    
    modifiedWeeks.value.forEach(week => {
      const items = weekItems.value[week] || []
      items.forEach((item, index) => {
        changes.push({
          path_step_id: item.id,
          new_order: index + 1,
          new_week_order: parseInt(week)
        })
      })
    })

    if (changes.length > 0) {
      // Make batch API call
      const response = await api.post(API_ROUTES.REORDER_PATH_STEP_BATCH, {
        changes: changes
      })

      if (response.data) {
        emit('refresh-data')
        
        setTimeout(() => {
          isReordering.value = false
          showSuccess.value = true
          hasUnsavedChanges.value = false
          changedItemIds.value = new Set()
          modifiedWeeks.value.clear()
          
          setTimeout(() => {
            showSuccess.value = false
          }, 2000)
        }, 300)
      }
    }
  } catch (error) {
    console.error('Failed to save changes:', error)
    isReordering.value = false
    showError.value = true
    setTimeout(() => {
      showError.value = false
    }, 2000)
  }
}

// Define Emits
const emit = defineEmits<{
  (e: 'toggle-expand', id: number): void
  (e: 'refresh-data'): void
  (e: 'update:visible-week', week: string): void
}>()

// Replace Set with a single value ref
const expandedItemId = ref<number | null>(null)

const toggleExpand = async (id: number) => {
  // If clicking the currently expanded item, collapse it
  if (expandedItemId.value === id) {
    expandedItemId.value = null
  } else {
    // Otherwise, expand the clicked item (and collapse any previously expanded item)
    expandedItemId.value = id
    // Optionally emit toggle-expand if parent needs to know
    emit('toggle-expand', id)
  }
}

const handleSaved = () => {
  // Close the expanded item
  expandedItemId.value = null
  // Emit an event to parent to refresh the data
  emit('refresh-data')
}

// Add new ref for initial loading state and content ready state
const isInitialLoad = ref(true)
const isContentReady = ref(false)

const weekElements = ref<HTMLElement[]>([])
const observer = ref<IntersectionObserver | null>(null)

// Update the onMounted hook
onMounted(() => {
  // Set initial load to false after a short delay
  setTimeout(() => {
    isInitialLoad.value = false
  }, 500)

  observer.value = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const weekId = entry.target.id.replace('week-', '')
          emit('update:visible-week', weekId)
        }
      })
    },
    { threshold: 0.5 }
  )

  // Watch for changes in weekElements and observe them
  watch(
    weekElements,
    (newElements) => {
      if (observer.value) {
        observer.value.disconnect()
        newElements.forEach((el) => {
          if (el) observer.value?.observe(el)
        })
      }
    },
    { deep: true, immediate: true }
  )
})

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect()
  }
})

// Add watch for weekItems to track when content is ready
watch(() => weekItems.value, (newValue) => {
  if (Object.keys(newValue).length > 0) {
    isContentReady.value = true
  }
}, { immediate: true })

// Track when content is fully ready to display (rows have rendered)
const contentRoot = ref<HTMLElement | null>(null)
const hasRenderedContent = ref(false)

const isContentFullyReady = computed(() => {
  return isContentReady.value && hasRenderedContent.value && !props.isLoading
})

// Check if DOM has actually rendered the table rows
const checkContentRendered = () => {
  if (!contentRoot.value) return false
  const rows = contentRoot.value.querySelectorAll('tbody tr')
  return rows.length > 0
}

// Watch for content ready state and verify DOM rendering
watch([() => isContentReady.value, () => props.isLoading], async ([ready, loading]) => {
  if (ready && !loading) {
    await nextTick()
    const start = performance.now()
    const checkRendered = () => {
      if (checkContentRendered() || performance.now() - start > 2000) {
        hasRenderedContent.value = true
      } else {
        requestAnimationFrame(checkRendered)
      }
    }
    requestAnimationFrame(checkRendered)
  } else {
    hasRenderedContent.value = false
  }
}, { immediate: true })

// Reset modified weeks when data changes
watch(() => props.pathStepsByWeek, () => {
  if (!isReordering.value) {
    setTimeout(() => {
      if (!isReordering.value) {
        weekItems.value = { ...processedData.value }
        originalState.value = JSON.parse(JSON.stringify(processedData.value))
        hasUnsavedChanges.value = false
        changedItemIds.value = new Set()
        modifiedWeeks.value.clear()
      }
    }, 50)
  }
}, { deep: true })

// Delete functionality
const pathStepsStore = usePathStepsStore()
const isDeleting = computed(() => pathStepsStore.isDeleting)
const showDeleteModal = ref(false)
const itemToDelete = ref<WeeklyTaskItem | null>(null)

const confirmDelete = (item: WeeklyTaskItem) => {
  if (!canEdit.value) return
  itemToDelete.value = item
  showDeleteModal.value = true
}

const deleteItem = async () => {
  if (!canEdit.value) return
  if (!itemToDelete.value) return
  
  const success = await pathStepsStore.deletePathStep(itemToDelete.value.id)
  if (success) {
    showDeleteModal.value = false
    itemToDelete.value = null
    emit('refresh-data')
  }
}

// Add the points calculation function in the script section
const calculateWeekPoints = (items: WeeklyTaskItem[]): number => {
  return items.reduce((total, item) => total + (item.points || 0), 0)
}
</script>

<style>
.flip-list-move {
  transition: transform 0.3s ease;
}

.sortable-ghost {
  opacity: 0.5;
  background: #c8ebfb !important;
}

.sortable-drag {
  opacity: 0.9;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: .5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.bg-cosmicPurple\/30 {
  background-color: rgb(var(--cosmic-purple) / 0.3);
}

/* Add styles for selected items */
.selected-row {
  background-color: rgb(var(--cosmic-purple) / 0.1);
}

/* Add styles for dragging multiple items */
.dragging-item {
  background: white !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  opacity: 1 !important;
  z-index: 100 !important;
  transform: scale(1.02) !important;
}

.ghost-item {
  opacity: 0.5;
  background: #e8f4ff !important;
  border: 2px dashed #60a5fa !important;
}

.chosen-item {
  background: #f0f9ff !important;
}

/* Update selected row style */
.bg-blue-50 {
  background-color: rgb(var(--cosmic-purple) / 0.1) !important;
}

.bg-blue-50:hover {
  background-color: rgb(var(--cosmic-purple) / 0.15) !important;
}

/* Add transition for selection */
tr {
  transition: background-color 0.2s ease;
}

/* Add multi-drag styles */
.multi-drag-item {
  position: fixed;
  pointer-events: none;
  z-index: 1000;
  opacity: 0.8;
  background: white;
  padding: 8px;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  font-size: 0.875rem;
  transform: translate3d(var(--drag-x, 0), var(--drag-y, 0), 0);
  will-change: transform;
  transition: transform 0.1s ease-out;
}

/* Improve fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.1s ease-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-move {
  transition: transform 0.1s ease-out;
}

/* Ensure proper cursor states */
.drag-handle {
  cursor: grab;
}

tr:not(.dragging-item) .drag-handle:active {
  cursor: grabbing;
}

.dragging-item,
.dragging-item * {
  cursor: grabbing !important;
}

/* Improve drag handle visibility */
.drag-handle:hover {
  color: var(--cosmic-purple);
}
</style>